import React, { useState, useEffect, FC } from 'react'
import Helmet from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useParams } from 'react-router-dom'
import { delMfaAuth, fetchMfaQrCode, updateMfaAuth } from '../../../../states/containers/setting/systemSlice'
import { RootState } from '../../../../states/containers/store'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { useAlert } from 'react-alert'

type LangValue = 'ja' | 'en'

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { language } = useParams<{ language: LangValue }>()
  const alert = useAlert()

  const {
    commonPart,
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [inputMfaCode, setInputMfaCode] = useState<string>()
  const [isError, setIsError] = useState<boolean>(false)
  const [qrCodeUrl, setQrCodeUrl] = useState<string>()
  const [secretKey, setSecretKey] = useState<string>('')

  const saveMfa = () => {
    setIsError(false)
    dispatch(
      updateMfaAuth(
        {
          mfa_code: inputMfaCode,
        },
        () => {
          alert.success(t('setting.personal.mfa.alert.set'))
          history.push(setPath(URL.SETTING_MFA_AUTH.path, language))
        },
        () => {
          setIsError(true)
        },
      ),
    )
  }

  const disableMfaAuth = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('setting.personal.mfa.confirm.text'))) {
      dispatch(
        delMfaAuth(
          () => {
            alert.success(t('setting.personal.mfa.alert.delete'))
          },
        ),
      )
    }
  }

  useEffect(() => {
    if (!login.accessToken) return
    if (commonPart.current_member?.mfa_flg) return
    dispatch(
      fetchMfaQrCode(
        (result) => {
          setQrCodeUrl(result.url)
          setSecretKey(result.google_secret)
        },
      ),
    )
  }, [login.accessToken])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - 個人設定 - ${t('setting.personal.mfa.title')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_PERSONAL.path, language)}
        >
          個人設定
        </Link>
        &nbsp;&gt;&nbsp;
        {t('setting.personal.mfa.title')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {t('setting.personal.mfa.title')}
        </h1>
      </section>
      <section className="page_contents">
        {
          commonPart.current_member?.mfa_flg ? (
            <>
              <div className="content-block">
                <div className="block-detail">
                  <p>
                    {t('setting.personal.mfa.enabled')}
                  </p>
                  <button
                    className="button delete"
                    type="button"
                    onClick={disableMfaAuth}
                  >
                    {t('setting.personal.mfa.button.delete')}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="content-block">
                <div className="block-detail">
                  <div className="block-header">
                    <div className="title">
                      {t('setting.personal.mfa.section1.title')}
                    </div>
                  </div>
                  <div className="block-detail">
                    <div className="description">
                      {t('setting.personal.mfa.section1.description')}
                    </div>
                    <div>
                      <a
                        href="https://apps.apple.com/jp/app/google-authenticator/id388497605"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('setting.personal.mfa.section1.apple')}
                      </a>
                    </div>
                    <div>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=ja&gl=US"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('setting.personal.mfa.section1.google')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="content-block">
                <div className="block-detail">
                  <div className="block-header">
                    <div className="title">
                      {t('setting.personal.mfa.section2.title')}
                    </div>
                  </div>
                  <div className="block-detail">
                    <div className="description">
                      {t('setting.personal.mfa.section2.description')}
                    </div>
                    <div className="qr_code">
                      {qrCodeUrl && (<img src={qrCodeUrl} alt="qr_code" />)}
                    </div>
                    <p>
                      {t('setting.personal.mfa.section2.text')}
                    </p>
                    <div className="mfa_input">
                      <p>
                        {t('setting.personal.mfa.section2.account')}
                      </p>
                      <input
                        type="text"
                        value={`Qasee (${commonPart.current_member?.email})`}
                        onFocus={(e) => e.currentTarget.select()}
                        readOnly
                      />
                    </div>
                    <div className="mfa_input">
                      <p>
                        {t('setting.personal.mfa.section2.secret_key')}
                      </p>
                      <input
                        type="text"
                        value={secretKey}
                        onFocus={(e) => e.currentTarget.select()}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="content-block">
                <div className="block-detail">
                  <div className="block-header">
                    <div className="title">
                      {t('setting.personal.mfa.section3.title')}
                    </div>
                  </div>
                  <div className="block-detail mfa">
                    <div className="description">
                      {t('setting.personal.mfa.section3.description')}
                    </div>
                    <input
                      type="text"
                      onChange={(event) => setInputMfaCode(event.target.value)}
                      value={inputMfaCode}
                      placeholder={t('setting.personal.mfa.section3.placeholder')}
                    />
                    <button
                      className="button"
                      type="button"
                      onClick={saveMfa}
                    >
                      {t('setting.personal.mfa.section3.button')}
                    </button>
                    {
                      isError && (
                        <div className="form-error">
                          {t('setting.personal.mfa.section3.error')}
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            </>
          )
        }

      </section>
    </section>
  )
}

export default Index
